import { ErrorAlert } from '@e2m-customer-portal/ui';
import { CircularProgress } from '@mui/material';
import clsx from 'clsx';
import { ComponentType, ReactElement, ReactNode, Suspense } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

interface DrawerContentProps {
  className?: string;
  errorFallback?: ComponentType<FallbackProps> | string | number | boolean;
  disableErrorHandler?: boolean;
  suspenseFallback?: ReactNode;
  disableSuspenseHandler?: ReactNode;
  children?: ReactNode;
  unknownErrorLabel: string;
}

/**
 * DrawerContent can be used to add themed content to the Drawer with an ErrorBoundary.
 */
export function DrawerContent({
  className,
  errorFallback,
  disableErrorHandler,
  suspenseFallback,
  disableSuspenseHandler,
  children,
  unknownErrorLabel,
}: Readonly<DrawerContentProps>): ReactElement<DrawerContentProps> {
  const withSuspense = disableSuspenseHandler ? (
    children
  ) : (
    <Suspense
      fallback={
        suspenseFallback ?? (
          <div className="flex h-[12.5rem] items-center justify-center">
            <CircularProgress />
          </div>
        )
      }
    >
      {children}
    </Suspense>
  );

  let fallbackRenderer = errorFallback;
  if (errorFallback && ['string', 'number', 'boolean'].includes(typeof errorFallback)) {
    fallbackRenderer = function ErrorFallback() {
      return <>{errorFallback}</>;
    };
  } else if (!errorFallback) {
    fallbackRenderer = function ErrorFallback({ error }: Readonly<{ error: Error }>) {
      return <ErrorAlert error={error}>{unknownErrorLabel}</ErrorAlert>;
    };
  }

  const withErrorBoundary = disableErrorHandler ? (
    withSuspense
  ) : (
    <ErrorBoundary fallbackRender={fallbackRenderer as never}>{withSuspense}</ErrorBoundary>
  );

  return <div className={clsx('grow overflow-y-auto px-6 py-0', className)}>{withErrorBoundary}</div>;
}
